import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import usePubnubInstance from "./_pubnub-unread-messages";

import FILE_SIZE from "$/settings/enums/files/_size.json";
import { useChatStore } from "~/stores/chatStore";

let { maxFileSizeInMegabytes } = FILE_SIZE;

/**
 * Pub nub hook.
 *
 * @param {Object} instance Chat instance from pub nub.
 * @param {Array} channels Channels for chat.
 * @param {Object} userInfo Info about user, injected from pubnub.
 */
export default (
  instance,
  channels,
  internalNotesChannels,
  user,
  ticketId = "",
  isInternal,
) => {
  if (!instance || !channels) return;

  const { messagesIntl } = useIntl();
  const pubnubInstance = usePubnubInstance(instance)();

  const channelIdsKey = (channels ?? []).join("\0");
  const internalChannelIdsKey = (internalNotesChannels ?? []).join("\0");

  const setChatOptions = useChatStore((s) => s.setChatOptions);

  const subscribeRef = useRef([]);
  const isFetchingRef = useRef(false);
  const isFetchingInternalRef = useRef(false);
  const isLoadingRef = useRef(true);

  const storedDownlaodsRef = useRef({});

  const [messages, setMessages] = useState([]);
  const [internalMessages, setInternalMessages] = useState([]);
  const [externalMessagesCount, setExternalMessagesCount] = useState();
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [internalMessagesCount, setInternalMessagesCount] = useState();
  const [hasMoreInternalMessages, setHasMoreInternalMessages] = useState(true);
  const [loadMore, setLoadMore] = useState(0);
  const [loadMoreInternal, setLoadMoreInternal] = useState(0);
  const [newMessage, setNewMessage] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingInternal, setIsFetchingInternal] = useState(false);

  /**
   * Get messages count
   *
   * @return {Void}.
   */
  const getMessagesCount = async () => {
    let messagesCount;
    isLoadingRef.current = true;

    const internalChannels = internalNotesChannels ?? [];
    const countChannels = isInternal
      ? [...channels, ...internalChannels]
      : channels;

    try {
      const r = await instance.messageCounts({
        channels: countChannels,
        channelTimetokens: countChannels.map(() => Date.now()),
      });

      messagesCount = r.channels[channels[0]];
      setExternalMessagesCount(messagesCount ?? 0);
      if (isInternal && internalChannels[0]) {
        setInternalMessagesCount(r.channels[internalChannels[0]] ?? 0);
      }
    } catch (status) {
      if (status?.error) {
        console.error("Unable to get chat message counts", status);
      }
      setExternalMessagesCount((prev) => (prev === undefined ? 0 : prev));
      if (isInternal) {
        setInternalMessagesCount((prev) => (prev === undefined ? 0 : prev));
      }
    } finally {
      isLoadingRef.current = false;
    }
  };

  /**
   * Send message to the channel.
   *
   * @param {String} messageText Message text.
   * @param {Function} callback Function after message is send.
   * @return {void}
   */
  let sendChatMessage = async (
    messageText,
    callback,
    internal,
    isFileMessage = false,
    customMeta = {},
  ) => {
    if (messageText) {
      try {
        await instance.publish({
          channel: internal ? internalNotesChannels[0] : channels[0],
          message: {
            messageType: "text",
            text: messageText,
          },
          ...((isFileMessage || customMeta) && {
            meta: { isFileMessage, isInternal: internal, ...customMeta },
          }),
        });
        callback();
      } catch (status) {
        if (status?.error) {
          toast(
            <Toast
              message={messagesIntl?.chat?.messagedFailed}
              type={TOAST_TYPES.ERROR}
            />,
            {
              closeButton: false,
            },
          );
          console.error("Unable to send chat message", status?.error);
        }
      }
    }
  };

  /**
   * Get file by id.
   * @param {String} fileId File id.
   * @param {String} fileName Name
   * @return {Object}
   */
  let getFileById = (fileId, fileName) => {
    // console.log("getFileById  in chat");
    const result = instance.getFileUrl({
      channel: channels[0],
      id: fileId,
      name: fileName,
    });

    return result;
  };

  /**
   * Start download on click.
   *
   * @param {Object} file File.
   * @return {void}
   */
  let downloadFileLocaly = (file, isInternal) => {
    // console.log("downloadFileLocaly in chat");
    return new Promise((resolve, reject) => {
      instance
        .downloadFile({
          channel: isInternal ? internalNotesChannels[0] : channels[0],
          id: file.id,
          name: file.name,
        })
        .then((response) => {
          let link = document.createElement("a");
          link.setAttribute("download", response.name);

          document.body.appendChild(link);

          link.href = URL.createObjectURL(response.data);
          link.target = "_blank";
          link.click();
          resolve(true);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  /**
   * On download hook.
   *
   * @param {Object} file File.
   * @return {Promise}
   */
  let onDownload = (file, isInternal) => {
    if (storedDownlaodsRef.current[file.id]) {
      return new Promise((resolve) =>
        resolve(storedDownlaodsRef.current[file.id]),
      );
    }
    return new Promise((resolve, reject) => {
      instance
        .downloadFile({
          channel: isInternal ? internalNotesChannels[0] : channels[0],
          id: file.id,
          name: file.name,
        })
        .then((response) => {
          const obj = {
            type: response.data.type,
            url: URL.createObjectURL(response.data),
          };
          storedDownlaodsRef.current[file.id] = obj;
          resolve(obj);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  /**
   * Send file and in message send file type.
   *
   * @param {Object} file File
   * @return {void}
   */
  let sendFile = (file, internal) => {
    const bytes = file.size;
    const bytesToMegaBytes = bytes / (1024 * 1024);
    const rounded = Math.round((bytesToMegaBytes + Number.EPSILON) * 100) / 100;

    if (rounded > maxFileSizeInMegabytes) {
      return;
    }

    instance.objects
      .getUUIDMetadata({
        uuid: user?.chatUUUID,
      })
      .then((response) => {
        const { data = {} } = response || {};
        if (data.externalId === user?._id) {
          let messageTime = (new Date().getTime() + 1 * 60 * 1000) * 10000;

          if (internal) {
            setInternalMessages((prev) => [
              ...prev,
              {
                timetoken: messageTime.toString(),
                message: {
                  sending: true,
                  file: {},
                  sender: { ...data, ...data.custom },
                },
              },
            ]);
            return;
          }

          setMessages((prev) => [
            ...prev,
            {
              timetoken: messageTime.toString(),
              message: {
                sending: true,
                file: {},
                sender: { ...data, ...data.custom },
              },
            },
          ]);
        }
      })
      .catch((err) => {
        toast(<Toast message={err.message} type={TOAST_TYPES.ERROR} />, {
          closeButton: false,
        });
      });

    return instance.sendFile({
      channel: internal ? internalNotesChannels[0] : channels[0],
      message: {
        text: file?.name,
        type: file.type,
        size: bytes,
      },
      file,
    });
  };

  /**
   * Grouped messages by date.
   *
   * @param {Object} data Ticket.
   * @return Object
   */
  let groupedByDate = function (data) {
    // console.log("groupedByDate in chat");
    const grouped = data.reduce((obj, message) => {
      const date = new Date(parseInt(message.timetoken) / 10000);
      const key =
        date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
      if (!obj[key]) obj[key] = [];
      obj[key].push(message);
      return obj;
    }, {});

    if (Object.keys(grouped).length === 0) return data;

    const transformed = {
      grouped: Object.entries(grouped)
        .map(([key, messages]) => ({
          date: new Date(key.replace(/-/g, "/")),
          messages: messages.sort((a, b) => a.timetoken - b.timetoken),
        }))
        ?.sort((a, b) => a.date.getTime() - b.date.getTime()),
    };

    return transformed;
  };
  /**
   * Get messages for channel
   *
   * @param {Object} dateRange Start and end date.
   */
  const getMessages = async (dateRange = {}) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);

    // Every caller is fire-and-forget (the mount effect, and the load-more
    // effect), so a rejected history read surfaced only as an uncaught
    // PubNubError — and, worse, skipped the two lines that clear the in-flight
    // flags at the end of this function. The chat then sat on a spinner for the
    // rest of the session: `isFetchingRef` stayed true, which is exactly what
    // the load-more effect checks before trying again.
    try {
      const response = await instance.fetchMessages({
        channels: channels,
        ...dateRange,
        count: 25,
        includeMeta: true,
      });
      if (response) {
        let messagesData = response.channels[channels[0]];
        const allPublishers = [
          ...new Set([...(messagesData?.map?.((m) => m.uuid) ?? [])]),
        ];
        const publishersMetadata = await instance.objects.getAllUUIDMetadata({
          customFields: true,
          include: {
            customFields: true,
          },
          filter: allPublishers
            .map((p) => `custom.uuid == "${p}"`)
            .join(" || "),
        });
        const publishersMap = allPublishers.reduce((obj, publisher) => {
          return {
            ...obj,
            [publisher]: publishersMetadata.data.find(
              (p) => p.id === publisher,
            ),
          };
        }, {});

        if (messagesData?.length) {
          messagesData?.map((item) => {
            if (item.message.file) {
              item.message = {
                file: item.message.file,
                text: item.message?.message?.text,
                type: item.message?.message?.type,
                size: item.message?.message?.size,
              };
              item.message.file = {
                ...item?.message?.file,
                url: getFileById(
                  item?.message?.file?.id,
                  item?.message?.file?.name,
                ),
              };
            }
            item.message.sender = {
              ...(publishersMap?.[item.uuid] ?? {}),
              ...(publishersMap?.[item.uuid]?.custom ?? {}),
            };
          });
          const allMessages = [...messagesData, ...messages].filter(
            (msg, index, msgs) =>
              msgs.findLastIndex(
                (el) =>
                  el.message.sender?.id === msg.message.sender?.id &&
                  el.timetoken === msg.timetoken,
              ) === index,
          );
          setMessages(allMessages);
        }
        if (messagesData?.length < 25 || !messagesData?.length) {
          setHasMoreMessages(false);
        }
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      // The SDK's message is the generic "REST API request processing error";
      // the channels are the only diagnosis available, and a PAM denial on one
      // of them fails the whole request.
      console.error("[chat] fetchMessages failed", { channels, error });
      // Same treatment as an empty response: there is nothing further to show,
      // and leaving this true would have the list ask for more on every scroll.
      setHasMoreMessages(false);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
    }
  };

  const getInternalNotes = async (dateRange = {}) => {
    if (isFetchingInternalRef.current) return;

    isFetchingInternalRef.current = true;
    setIsFetchingInternal(true);

    // See `getMessages` — same fire-and-forget callers, same stuck-spinner
    // failure. Internal notes are the likelier one to be refused outright: a
    // portal contact can hold a membership on one without a read grant.
    try {
      const response = await instance.fetchMessages({
        channels: internalNotesChannels,
        ...dateRange,
        count: 25,
        includeMeta: true,
      });
      if (response) {
        let messagesData = response.channels[internalNotesChannels[0]];

        const allPublishers = [
          ...new Set([...(messagesData?.map?.((m) => m.uuid) ?? [])]),
        ];
        const publishersMetadata = await instance.objects.getAllUUIDMetadata({
          customFields: true,
          include: {
            customFields: true,
          },
          filter: allPublishers
            .map((p) => `custom.uuid == "${p}"`)
            .join(" || "),
        });

        const publishersMap = allPublishers.reduce((obj, publisher) => {
          return {
            ...obj,
            [publisher]: publishersMetadata.data.find(
              (p) => p.id === publisher,
            ),
          };
        }, {});

        if (messagesData?.length) {
          let arr = [...messagesData];

          arr.map((item) => {
            item.sender = publishersMap[item.uuid];

            if (item.message.file) {
              item.message = {
                file: item.message.file,
                text: item.message?.message?.text,
                type: item.message?.message?.type,
                size: item.message?.message?.size,
              };
              item.message.file = {
                ...item?.message?.file,
                url: getFileById(
                  item?.message?.file?.id,
                  item?.message?.file?.name,
                ),
              };
            }
            return item;
          });

          setInternalMessages((prev) =>
            [...arr, ...prev].filter(
              (msg, index, msgs) =>
                msgs.findLastIndex(
                  (el) =>
                    el.message.sender?.id === msg.message.sender?.id &&
                    el.timetoken === msg.timetoken,
                ) === index,
            ),
          );
        }
        if (messagesData?.length < 25 || !messagesData?.length) {
          setHasMoreInternalMessages(false);
        }
      } else {
        setHasMoreInternalMessages(false);
      }
    } catch (error) {
      console.error("[chat] fetchMessages (internal notes) failed", {
        channels: internalNotesChannels,
        error,
      });
      setHasMoreInternalMessages(false);
    } finally {
      isFetchingInternalRef.current = false;
      setIsFetchingInternal(false);
    }
  };

  /**
   * Attach pub nub listeneres.
   * @return {Object} Listeners
   */
  let attachListeners = {
    message: (m) => {
      if (m && !(isFetchingInternalRef.current || isFetchingRef.current)) {
        if (!m.message?.messageType) return;
        instance.objects
          .getUUIDMetadata({
            uuid: m.publisher,
          })
          .then((response) => {
            const { data = {} } = response || {};
            if (
              m.channel.includes("internal-notes") &&
              subscribeRef.current.includes(m.channel)
            ) {
              setNewMessage({
                externalSender: false,
                time: m.timetoken,
              });
              setInternalMessages((prevNotes) => {
                const msgs = [
                  ...prevNotes,
                  {
                    ...m,
                    message: {
                      ...m.message,
                    },
                    sender: { ...data, ...data.custom },
                    userMetadata: m.userMetadata,
                  },
                ].filter(
                  (msg, index, arr) =>
                    arr.findIndex((el) => el.timetoken === msg.timetoken) ===
                    index,
                );

                return msgs;
              });
              setInternalMessagesCount((prev) => prev + 1);
            } else {
              const isExternalSender = data.externalId !== user?._id;
              setNewMessage({
                externalSender: isExternalSender,
                time: m.timetoken,
              });

              if (ticketId && m.channel.includes(ticketId))
                setMessages((prevMessages) => {
                  if (
                    prevMessages.some((msg) => msg.timetoken === m.timetoken)
                  ) {
                    return prevMessages;
                  }
                  return [
                    ...prevMessages,
                    {
                      ...m,
                      message: {
                        ...m.message,
                        sender: { ...data, ...data.custom },
                      },
                    },
                  ];
                });
              setExternalMessagesCount((prev) => prev + 1);
            }
            const isExternalSender = data.externalId !== user?._id;
            if (!isExternalSender) {
              pubnubInstance.updateSingleMembership({
                id: m.channel,
                custom: {
                  lastReadTimetoken: parseInt(
                    m?.timetoken ?? new Date().getTime() * 10000,
                  ),
                },
              });
            }
          })
          .catch((err) => {
            toast(<Toast message={err.message} type={TOAST_TYPES.ERROR} />, {
              closeButton: false,
            });
          });
      }
    },
    file: (m) => {
      if (m && !(isFetchingInternalRef.current || isFetchingRef.current)) {
        instance.objects
          .getUUIDMetadata({
            uuid: m.publisher,
          })
          .then((response) => {
            const { data = {} } = response || {};

            let obj = {
              ...m,
              message: {
                ...m.message,
                sending: false,
                sender: { ...data, ...data.custom },
                file: m.file,
              },
            };

            if (
              m.channel.includes("internal-notes") &&
              subscribeRef.current.includes(m.channel)
            ) {
              setNewMessage({
                externalSender: false,
                time: m.timetoken,
              });
              setInternalMessages((prev) => {
                let newMessages = [...prev];
                const filteredMessages = newMessages.filter(
                  (message) => !message?.message?.sending,
                );
                const alreadyExists = newMessages.some(
                  (message) =>
                    message.message?.file?.id === obj.message.file?.id,
                );
                if (!alreadyExists) filteredMessages.push(obj);
                return filteredMessages;
              });
            } else {
              setNewMessage({
                externalSender: data.externalId !== user?._id,
                time: m.timetoken,
              });

              if (ticketId && m.channel.includes(ticketId))
                setMessages((prev) => {
                  const newMessages = prev.filter(
                    (message) => !message?.message?.sending,
                  );
                  newMessages.push(obj);
                  return [
                    ...newMessages,
                    ...(data.externalId !== user?._id ? [obj] : []),
                  ];
                });
            }

            const isExternalSender = data.externalId !== user?._id;
            if (!isExternalSender) {
              pubnubInstance.updateSingleMembership({
                id: m.channel,
                custom: {
                  lastReadTimetoken: parseInt(
                    m?.timetoken ?? new Date().getTime() * 10000,
                  ),
                },
              });
            }
          })
          .catch((err) => {
            toast(<Toast message={err.message} type={TOAST_TYPES.ERROR} />, {
              closeButton: false,
            });
          });
      }
    },
  };

  /**
   * After new message is set,
   * update store.
   */
  useEffect(() => {
    setChatOptions({ pubnub: { newMessage } });
  }, [newMessage]);

  /**
   * On channel effect, subscribe or unsubsribe
   * get messages number in channel
   * and get messages for that period.
   *
   * @return {Function} Unsubscribe on component unmount.
   */
  useEffect(() => {
    let arr;

    const allChannels = [...channels, ...internalNotesChannels];

    const channelsNotSubscribed = allChannels.filter(
      (element) => !subscribeRef.current.includes(element),
    );

    if (channelsNotSubscribed?.length) {
      arr = [...(isInternal ? internalNotesChannels : channels)];

      instance.subscribe({ channels: arr, withPresence: false });
      subscribeRef.current = Array.from(
        new Set([...arr, ...subscribeRef.current]),
      );

      if (!externalMessagesCount || (isInternal && !internalMessagesCount))
        getMessagesCount();
      if (isInternal) getInternalNotes();
      else getMessages();

      setChatOptions({
        pubnub: {
          newMessage: false,
          groupedByDate,
          messages,
          setMessages,
          messagesCount: externalMessagesCount,
          loadMore,
          onLoadMore: setLoadMore,
          sendMessage: sendChatMessage,
          sendFile,
          onDownload,
          downloadFileLocaly,
        },
      });
    }
  }, [channelIdsKey, internalChannelIdsKey, isInternal]);

  useEffect(() => {
    return () => {
      instance.unsubscribe({ channels: subscribeRef.current });

      setChatOptions({ pubnub: {} });
      subscribeRef.current = [];
    };
  }, [channelIdsKey, internalChannelIdsKey]);

  /**
   * Add listener and remove listener
   * on first load.
   *
   * @return {Function} Remove listener.
   */
  useEffect(() => {
    instance.addListener(attachListeners);

    return () => {
      instance.removeListener(attachListeners);
    };
  }, [messages.length, internalMessages.length]);

  /**
   * Get messages from time
   * when first message from prev render was sent.
   */
  useEffect(() => {
    if (!isFetchingRef.current && subscribeRef.current.includes(channels[0])) {
      getMessages({
        start: messages?.[0]?.timetoken,
      });
    }
  }, [loadMore]);

  useEffect(() => {
    if (
      !isFetchingInternalRef.current &&
      subscribeRef.current.includes(internalNotesChannels[0])
    ) {
      getInternalNotes({
        start: internalMessages?.[0]?.timetoken,
      });
    }
  }, [loadMoreInternal]);

  return {
    groupedByDate,
    messages,
    setMessages,
    internalMessages,
    messagesCount: externalMessagesCount,
    internalMessagesCount,
    loadMore,
    loadMoreInternal,
    onLoadMore: setLoadMore,
    onLoadMoreInternal: setLoadMoreInternal,
    sendMessage: sendChatMessage,
    sendFile,
    isFetching: isFetching,
    isFetchingInternal: isFetchingInternal,
    isLoading: isLoadingRef.current,
    hasMoreMessages,
    hasMoreInternalMessages,
  };
};
